/**
 * Artifact Manager
 * 
 * Handles insertion of Excel artifacts into PowerPoint presentations.
 * Supports two insertion modes:
 * - Image: Static PNG via Office.js (cross-platform)
 * - OLE: Editable Excel object via COM (Windows only)
 * 
 * Ensures artifacts are inserted into the correct presentation by deck ID matching.
 */

import { getOrCreatedocumentId, getdocumentId } from './documentIdManager';
import { applyShapeMetadata, getArtifactEntryByShape } from './metadataService';
import type { MetadataEntry } from './metadataTypes';
import { BACKEND_BASE_URL, backendClient } from './communication/backendClient';
import GlobalStateManager from '../main/globals/global-state/globalStateManager';
import { ExcelArtifact } from '../main/agents/linkedExcelEditor/tools/submitArtifact';
import { getContextResolver } from '../main/core/contextResolver';
import { getAspectRatioFromImage, resolveImagePosition, type PositionInput, type ResolvedPosition } from '../utils/imageAspectRatio';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Position and size for inserted shape.
 * For images: at least one of width/height required (other calculated from aspect ratio).
 * For OLE: both width and height should be provided.
 */
export interface InsertPosition {
    /** Left position in points */
    left: number;
    /** Top position in points */
    top: number;
    /** Width in points (optional for images if height provided) */
    width?: number;
    /** Height in points (optional for images if width provided) */
    height?: number;
}


/**
 * Insert options
 */
export interface InsertOptions {
    /** Slide index (1-based) to insert on - consistent with readSlide tool */
    slideIndex: number;
    /** Position and size */
    position: InsertPosition;
    /** Preferred insertion method */
    preferredMethod?: "image" | "ole";
    /** Force specific deck ID (for safety check) */
    targetdocumentId?: string;
    /** Alt text for accessibility */
    altText?: string;
}

/**
 * Result from artifact insertion
 */
export interface InsertResult {
    success: boolean;
    /** PowerPoint shape ID */
    shapeId?: string;
    /** Slide index where inserted */
    slideIndex?: number;
    /** Method used for insertion */
    method: "image" | "ole" | "picture" | "copy";
    /** Error message if failed */
    error?: string;
    /** Warnings */
    warnings?: string[];
}

/**
 * Options for copying presentation shapes
 */
export interface CopyShapeOptions {
    /** Slide index (1-based) to copy to - consistent with readSlide tool */
    slideIndex: number;
    /** Position and size */
    position: InsertPosition;
    /** Target deck ID */
    targetdocumentId?: string;
    /** Preserve original formatting */
    preserveFormatting?: boolean;
}


// ═══════════════════════════════════════════════════════════════════════════
// ARTIFACT MANAGER CLASS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Manages insertion of Excel artifacts into PowerPoint.
 * 
 * Usage:
 * ```typescript
 * const manager = ArtifactManager.getInstance();
 * const result = await manager.insertArtifact(artifactId, {
 *     slideIndex: 1,  // 1-based (first slide)
 *     position: { left: 100, top: 100, width: 400, height: 300 },
 *     preferredMethod: "ole",
 *     targetdocumentId: "deck_abc123"  // Safety: ensure we're on right presentation
 * });
 * ```
 */
export class ArtifactManager {
    private static instance: ArtifactManager;
    
    private constructor() {}
    
    public static getInstance(): ArtifactManager {
        if (!ArtifactManager.instance) {
            ArtifactManager.instance = new ArtifactManager();
        }
        return ArtifactManager.instance;
    }
    
    
    /**
     * Insert an artifact into PowerPoint.
     * 
     * @param artifactId The artifact ID to insert
     * @param options Insert options (slide, position, method)
     * @returns InsertResult
     */
    public async insertArtifact(
        artifactId: string,
        options: InsertOptions
    ): Promise<InsertResult> {
        const warnings: string[] = [];
        
        try {
            // 1. Verify deck ID matches (safety check)
            if (options.targetdocumentId) {
                const currentdocumentId = await getdocumentId();
                if (currentdocumentId && currentdocumentId !== options.targetdocumentId) {
                    return {
                        success: false,
                        method: options.preferredMethod || "image",
                        error: `Deck ID mismatch: expected ${options.targetdocumentId}, current is ${currentdocumentId}. Are you on the correct presentation?`,
                    };
                }
            }
            
            // 2. Get artifact from globalStateManager
            // Note: getContext returns a ResolvedContent wrapper { id, prefix, content, storedAt }
            // The actual artifact data is in the .content property
            const globalState = GlobalStateManager.getInstance();
            const resolved = globalState.getContext(artifactId);
            const artifact = resolved?.content as ExcelArtifact | undefined;
            
            console.log(`[ArtifactManager] Retrieved artifact for ID '${artifactId}':`, resolved ? 'found' : 'NOT FOUND');
            if (artifact) {
                console.log(`[ArtifactManager] Artifact properties:`, {
                    artifactId: artifact.artifactId,
                    excelId: artifact.excelId,
                    artifactType: artifact.artifactType,
                    hasImageBase64: !!artifact.imageBase64,
                    imageBase64Length: artifact.imageBase64?.length || 0,
                    oleFilePath: artifact.oleFilePath,
                });
            }
            
            if (!artifact) {
                return {
                    success: false,
                    method: options.preferredMethod || "image",
                    error: `Artifact not found: ${artifactId}. Has it expired?`,
                };
            }
            
            // 3. Determine insertion method
            // Both "image" and "ole" modes use COM bridge with CopyPicture/Copy
            // Only fall back to Office.js image insertion if COM fails
            let result: InsertResult;
            
            if (artifact.oleFilePath) {
                // Use COM bridge for both image and OLE modes
                // - "image" mode: uses CopyPicture() -> pastes as EMF (aspect ratio preserved)
                // - "ole" mode: uses Copy() -> pastes as editable Excel object
                const modeDesc = options.preferredMethod === 'ole' ? 'OLE' : 'image (EMF)';
                console.log(`[ArtifactManager] Attempting COM insertion (${modeDesc}) for artifact ${artifactId}`);
                console.log(`[ArtifactManager] OLE file path: ${artifact.oleFilePath}`);
                try {
                    result = await this.insertViaCom(artifact, options);
                    console.log(`[ArtifactManager] COM result:`, result);
                    if (!result.success && options.preferredMethod !== 'ole') {
                        // COM image insertion failed, fall back to Office.js image
                        console.warn(`[ArtifactManager] COM image insertion failed: ${result.error}, falling back to Office.js image`);
                        warnings.push(`COM insertion failed (${result.error}), falling back to Office.js image`);
                        result = await this.insertAsImage(artifact, options);
                    }
                } catch (comError) {
                    console.error(`[ArtifactManager] COM exception:`, comError);
                    if (options.preferredMethod !== 'ole') {
                        // Only fall back to Office.js for image mode
                        warnings.push(`COM error: ${comError}, falling back to Office.js image`);
                        result = await this.insertAsImage(artifact, options);
                    } else {
                        // OLE mode has no fallback
                        result = {
                            success: false,
                            method: "ole" as const,
                            error: comError instanceof Error ? comError.message : String(comError),
                        };
                    }
                }
            } else {
                // No OLE file path - use Office.js image insertion with pre-rendered image
                console.log(`[ArtifactManager] No oleFilePath available, using Office.js image insertion`);
                result = await this.insertAsImage(artifact, options);
            }
            
            // 4. If successful, store metadata in presentation AND on shape
            if (result.success && result.shapeId) {
                const currentdocumentId = options.targetdocumentId || await getOrCreatedocumentId();
                const insertionMode = options.preferredMethod === 'ole' ? 'ole' : 'image';
                
                // 4a. Register unified metadata entry (artifacts + citations) for this shape
                try {
                    await applyShapeMetadata({
                        slideId: options.slideIndex,
                        shapeId: String(result.shapeId),
                        documentId: currentdocumentId,
                        metadata: {
                            addEntries: [
                                {
                                    type: 'artifact',
                                    sourceContextId: artifact.artifactId,
                                    payload: {
                                        artifactId: artifact.artifactId,
                                        excelId: artifact.excelId,
                                        artifactType: artifact.artifactType,
                                        source: artifact.source,
                                        exportOptions: artifact.exportOptions,
                                        miniWorkbookPath: artifact.oleFilePath,
                                        exportedAt: new Date(artifact.createdAt * 1000).toISOString(),
                                        insertedAt: new Date().toISOString(),
                                        insertionMode: insertionMode,
                                        description: artifact.description,
                                    },
                                },
                            ],
                        },
                    });
                } catch (metadataError) {
                    console.warn('[ArtifactManager] Failed to apply unified metadata:', metadataError);
                }
                
            }
            
            // Add any warnings
            if (warnings.length > 0) {
                result.warnings = warnings;
            }
            
            return result;
            
        } catch (error) {
            return {
                success: false,
                method: options.preferredMethod || "image",
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }
    
    /**
     * Insert artifact as an image using Office.js
     * 
     * Strategy:
     * 1. Calculate final position using aspect ratio preservation
     * 2. Try PowerPointApi 1.8's shape.fill.setImage() - returns shape ID for tracking
     * 3. Fall back to Common API's setSelectedDataAsync() - more widely supported but no shape ID
     */
    private async insertAsImage(
        artifact: ExcelArtifact,
        options: InsertOptions
    ): Promise<InsertResult> {
        const artifactId = artifact?.artifactId || 'unknown';
        
        // Calculate final position with aspect ratio preservation
        const resolvedPosition = resolveImagePosition(options.position, artifact?.aspectRatio);
        const optionsWithResolvedPosition: InsertOptions = {
            ...options,
            position: resolvedPosition,
        };
        
        // Debug: Log artifact structure
        console.log(`[ArtifactManager] ========== IMAGE INSERTION DEBUG ==========`);
        console.log(`[ArtifactManager] Artifact object keys:`, artifact ? Object.keys(artifact) : 'artifact is null/undefined');
        console.log(`[ArtifactManager] artifactId:`, artifact?.artifactId);
        console.log(`[ArtifactManager] excelId:`, artifact?.excelId);
        console.log(`[ArtifactManager] artifactType:`, artifact?.artifactType);
        console.log(`[ArtifactManager] aspectRatio:`, artifact?.aspectRatio);
        console.log(`[ArtifactManager] Original position:`, options.position);
        console.log(`[ArtifactManager] Resolved position:`, resolvedPosition);
        
        // Debug: Check base64 format
        const base64Data = artifact?.imageBase64;
        console.log(`[ArtifactManager] imageBase64 exists:`, !!base64Data);
        console.log(`[ArtifactManager] imageBase64 length:`, base64Data?.length || 0);
        console.log(`[ArtifactManager] imageBase64 starts with:`, base64Data?.substring(0, 50) || 'N/A');
        console.log(`[ArtifactManager] has data URI prefix:`, base64Data?.startsWith('data:') || false);
        
        // Check if PowerPointApi 1.8 is available (has setImage on shape.fill)
        // @ts-ignore - Office.context.requirements may not be in type definitions
        const hasPowerPointApi18 = Office.context.requirements?.isSetSupported?.('PowerPointApi', '1.8') ?? false;
        
        console.log(`[ArtifactManager] PowerPointApi 1.8 available: ${hasPowerPointApi18}`);
        
        if (hasPowerPointApi18) {
            // Try PowerPointApi approach first (returns shape ID)
            try {
                console.log(`[ArtifactManager] Attempting PowerPointApi insertion...`);
                const result = await this.insertAsImageViaPowerPointApi(artifact, optionsWithResolvedPosition);
                console.log(`[ArtifactManager] PowerPointApi result:`, result);
                return result;
            } catch (e) {
                console.warn(`[ArtifactManager] PowerPointApi insertion failed, falling back to Common API:`, e);
                // Fall through to Common API
            }
        }
        
        // Use Common API (setSelectedDataAsync) - more widely supported
        console.log(`[ArtifactManager] Using Common API (setSelectedDataAsync)...`);
        const commonApiResult = await this.insertAsImageViaCommonApi(artifact, optionsWithResolvedPosition);
        console.log(`[ArtifactManager] Common API result:`, commonApiResult);
        console.log(`[ArtifactManager] ========== IMAGE INSERTION END ==========`);
        return commonApiResult;
    }
    
    /**
     * Insert image using PowerPointApi 1.8+ shape.fill.setImage()
     * Returns shape ID for metadata tracking
     */
    private async insertAsImageViaPowerPointApi(
        artifact: ExcelArtifact,
        options: InsertOptions
    ): Promise<InsertResult> {
        const artifactId = artifact?.artifactId || 'unknown';
        
        // Convert 1-based slideIndex to 0-based for Office.js array access
        const slideIndex0Based = options.slideIndex - 1;
        
        return PowerPoint.run(async (context) => {
            // Get the target slide
            const slides = context.presentation.slides;
            slides.load("items");
            await context.sync();
            
            if (slideIndex0Based < 0 || slideIndex0Based >= slides.items.length) {
                return {
                    success: false,
                    method: "image" as const,
                    error: `Slide index ${options.slideIndex} out of range (${slides.items.length} slides)`,
                };
            }
            
            const slide = slides.items[slideIndex0Based];
            
            // Create a rectangle shape
            const shape = slide.shapes.addGeometricShape(
                PowerPoint.GeometricShapeType.rectangle,
                {
                    left: options.position.left,
                    top: options.position.top,
                    width: options.position.width,
                    height: options.position.height,
                }
            );
            
            // Load shape to get ID first
            shape.load("id,name");
            await context.sync();
            
            // Apply the image as fill (PowerPointApi 1.8+)
            const base64Data = artifact.imageBase64;
            
            // @ts-ignore - setImage is PowerPointApi 1.8+
            shape.fill.setImage(base64Data);
            
            // Remove the default border/outline from the shape
            // @ts-ignore - lineFormat may not be in type definitions
            if (shape.lineFormat) {
                shape.lineFormat.visible = false;
            }
            
            await context.sync();
            
            // Note: Shape metadata (artifactId, source info, etc.) is set via CustomXmlParts
            // in the main insertArtifact method after insertion completes.
            // We don't set shape.name anymore to avoid polluting user-visible properties.
            
            console.log(`[ArtifactManager] Inserted image artifact ${artifactId} as shape ${shape.id} (PowerPointApi)`);
            
            return {
                success: true,
                shapeId: String(shape.id),
                slideIndex: options.slideIndex, // Return 1-based for consistency
                method: "image" as const,
            };
        });
    }
    
    /**
     * Insert image using Common API setSelectedDataAsync()
     * More widely supported but doesn't return shape ID
     */
    private insertAsImageViaCommonApi(
        artifact: ExcelArtifact,
        options: InsertOptions
    ): Promise<InsertResult> {
        const artifactId = artifact?.artifactId || 'unknown';
        
        return new Promise(async (resolve) => {
            // First, we need to navigate to the correct slide
            // setSelectedDataAsync inserts on the currently selected slide
            // navigateToSlide expects 1-based index (uses goToByIdAsync which is 1-based)
            try {
                await this.navigateToSlide(options.slideIndex);
            } catch (e) {
                console.warn(`[ArtifactManager] Could not navigate to slide ${options.slideIndex}:`, e);
                // Continue anyway - will insert on current slide
            }
            
            // Use Common API to insert image
            Office.context.document.setSelectedDataAsync(
                artifact.imageBase64,
                {
                    coercionType: Office.CoercionType.Image,
                    imageLeft: options.position.left,
                    imageTop: options.position.top,
                    imageWidth: options.position.width,
                    imageHeight: options.position.height,
                } as Office.SetSelectedDataOptions,
                (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                        console.error("[ArtifactManager] setSelectedDataAsync failed:", asyncResult.error?.message);
                        resolve({
                            success: false,
                            method: "image" as const,
                            error: asyncResult.error?.message || "setSelectedDataAsync failed",
                        });
                    } else {
                        console.log(`[ArtifactManager] Inserted image artifact ${artifactId} via Common API (no shape ID available)`);
                        resolve({
                            success: true,
                            // Note: setSelectedDataAsync doesn't return shape ID
                            shapeId: undefined,
                            slideIndex: options.slideIndex, // Return 1-based for consistency
                            method: "image" as const,
                        });
                    }
                }
            );
        });
    }
    
    /**
     * Navigate to a specific slide by index
     * @param slideIndex 1-based slide index (consistent with readSlide tool)
     */
    private navigateToSlide(slideIndex: number): Promise<void> {
        return new Promise((resolve, reject) => {
            // PowerPoint goToByIdAsync uses 1-based slide IDs
            // slideIndex is already 1-based, so use it directly
            Office.context.document.goToByIdAsync(
                slideIndex,
                Office.GoToType.Index,
                (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                        reject(asyncResult.error);
                    } else {
                        resolve();
                    }
                }
            );
        });
    }
    
    /**
     * Insert artifact via COM clipboard bridge.
     * 
     * This approach opens Excel headlessly, copies the chart/range to clipboard,
     * and pastes into PowerPoint. More reliable than AddOLEObject.
     * 
     * Supports two insertion modes:
     * - "image": Uses CopyPicture() to paste as static EMF picture (aspect ratio preserved)
     * - "ole": Uses Copy() to paste as editable Excel object
     * 
     * @param artifact The artifact to insert
     * @param options Insert options (includes preferredMethod)
     */
    private async insertViaCom(
        artifact: ExcelArtifact,
        options: InsertOptions
    ): Promise<InsertResult> {
        // Get current deck ID for the COM request
        const currentdocumentId = options.targetdocumentId || await getOrCreatedocumentId();
        const artifactId = artifact?.artifactId || 'unknown';
        const isChart = artifact.artifactType === 'chart';
        
        // Determine insertion mode from preferredMethod
        // "image" = CopyPicture (EMF), "ole" = Copy (OLE object)
        const insertionMode: "image" | "ole" = options.preferredMethod === 'ole' ? 'ole' : 'image';
        
        // Resolve position (ensure width/height are both set)
        // For images, aspect ratio is preserved by COM
        // For OLE, dimensions are exact
        const resolvedPosition = resolveImagePosition(options.position, artifact?.aspectRatio);
        
        const requestBody = {
            documentId: currentdocumentId,
            // PowerPoint COM uses 1-based slide index
            // options.slideIndex is already 1-based, so use it directly
            slideId: options.slideIndex,
            excel_file_path: artifact.oleFilePath,
            sheet_name: artifact.source.sheetName,
            artifact_type: artifact.artifactType,
            // For charts, pass chart_name; for ranges, pass range_ref
            chart_name: isChart ? artifact.source.sourceRef : undefined,
            range_ref: isChart ? undefined : artifact.source.sourceRef,  // Pass range for COM to copy correct area
            position: resolvedPosition,
            artifact_id: artifactId,
            link: false,  // Embedded, not linked
            insertion_mode: insertionMode,  // "image" = EMF picture, "ole" = editable object
        };
        
        console.log(`[ArtifactManager] ========== COM INSERTION START ==========`);
        console.log(`[ArtifactManager] Endpoint: ${BACKEND_BASE_URL}/com/insertExcelArtifactViaClipboard`);
        console.log(`[ArtifactManager] Artifact type: ${artifact.artifactType}`);
        console.log(`[ArtifactManager] Insertion mode: ${insertionMode}`);
        console.log(`[ArtifactManager] Request body:`, JSON.stringify(requestBody, null, 2));
        
        try {
            // Call Python backend COM bridge (clipboard-based approach)
            console.log(`[ArtifactManager] Sending fetch request to COM endpoint...`);
            const result = await backendClient.insertExcelArtifactViaClipboard(requestBody);
            console.log(`[ArtifactManager] COM response JSON:`, result);
            
            if (!result.success) {
                console.error(`[ArtifactManager] COM returned success=false:`, result.error || result.message);
                throw new Error(result.error || result.message || "OLE insertion failed");
            }
            
            const resultMethod = insertionMode === 'ole' ? 'ole' : 'image';
            console.log(`[ArtifactManager] ✅ COM SUCCESS - Inserted ${artifact.artifactType} ${artifactId} as ${insertionMode} (shape ${result.shape_id})`);
            console.log(`[ArtifactManager] ========== COM INSERTION END ==========`);
            
            return {
                success: true,
                shapeId: result.shape_id ? String(result.shape_id) : undefined,
                slideIndex: options.slideIndex, // Return 1-based for consistency
                method: resultMethod as "image" | "ole",
            };
            
        } catch (error) {
            const resultMethod = insertionMode === 'ole' ? 'ole' : 'image';
            console.error(`[ArtifactManager] ❌ COM (${insertionMode}) FAILED:`, error);
            console.log(`[ArtifactManager] ========== COM INSERTION END (ERROR) ==========`);
            return {
                success: false,
                method: resultMethod as "image" | "ole",
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // IMAGE ARTIFACT INSERTION (for logos, wikimedia, presentation images)
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Insert an image artifact using Office.js PowerPointApi 1.8+
     * 
     * Uses shape.fill.setImage() which:
     * - Creates a geometric shape (rectangle)
     * - Fills it with the base64 image
     * - Requires just the base64 string (no data URL prefix)
     * 
     * @param artifactId The image artifact ID (logo_*, wikimedia_*, or composite)
     * @param options Insert options (slide, position)
     * @returns InsertResult
     */
    public async insertImageArtifact(
        artifactId: string,
        options: InsertOptions
    ): Promise<InsertResult> {
        const contextResolver = getContextResolver();
        const imageData = contextResolver.getImage(artifactId);
        
        if (!imageData) {
            return {
                success: false,
                method: "picture",
                error: `Image not found in cache: ${artifactId}`,
            };
        }

        console.log(`[ArtifactManager] Inserting image artifact: ${artifactId}`);

        // Calculate aspect ratio from image (calculates visual dimensions if needed)
        // This handles cases where pixel dimensions include transparent padding
        let base64 = imageData.base64;
        if (base64.startsWith('data:')) {
            base64 = base64.split(',')[1];
        }
        const aspectRatio = await getAspectRatioFromImage(base64, imageData.metadata);
        console.log(`[ArtifactManager] Image aspect ratio: ${aspectRatio.toFixed(2)}`);

        // Resolve position with aspect ratio preservation
        const resolvedPosition = resolveImagePosition(options.position, aspectRatio);

        // Note: base64 was already extracted above for aspect ratio calculation

        // Check if PowerPointApi 1.8 is available
        // @ts-ignore - Office.context.requirements may not be in type definitions
        const hasPowerPointApi18 = Office.context.requirements?.isSetSupported?.('PowerPointApi', '1.8') ?? false;

        if (!hasPowerPointApi18) {
            console.warn('[ArtifactManager] PowerPointApi 1.8 not available for image insertion');
            return {
                success: false,
                method: "picture",
                error: "PowerPointApi 1.8 required for image insertion is not available",
            };
        }

        // Convert 1-based slideIndex to 0-based for Office.js array access
        const slideIndex0Based = options.slideIndex - 1;
        
        return PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            slides.load("items");
            await context.sync();

            if (slideIndex0Based < 0 || slideIndex0Based >= slides.items.length) {
                return {
                    success: false,
                    method: "picture" as const,
                    error: `Slide index ${options.slideIndex} out of range (${slides.items.length} slides)`,
                };
            }

            const slide = slides.items[slideIndex0Based];

            // Create rectangle shape as image container
            const shape = slide.shapes.addGeometricShape(
                PowerPoint.GeometricShapeType.rectangle,
                {
                    left: resolvedPosition.left,
                    top: resolvedPosition.top,
                    width: resolvedPosition.width,
                    height: resolvedPosition.height,
                }
            );

            shape.load("id");
            await context.sync();

            // Fill with image (PowerPointApi 1.8+)
            // @ts-ignore - setImage is PowerPointApi 1.8+
            shape.fill.setImage(base64);

            // Remove border from shape
            // @ts-ignore - lineFormat may not be in type definitions
            if (shape.lineFormat) {
                shape.lineFormat.visible = false;
            }

            await context.sync();

            console.log(`[ArtifactManager] Inserted image ${artifactId} as shape ${shape.id}`);

            return {
                success: true,
                shapeId: String(shape.id),
                slideIndex: options.slideIndex, // Return 1-based for consistency
                method: "picture" as const,
            };
        });
    }

    // ═══════════════════════════════════════════════════════════════════════
    // SHAPE COPY (for presentation shapes from PresentationLibrarySearchAgent)
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Copy a presentation shape to a new location via COM
     * 
     * Uses the copyShapes COM endpoint which handles cross-slide copying
     * with clipboard preservation.
     * 
     * @param sourceContextId Composite ID (deck_*|slide_*|shape_*)
     * @param options Copy options (target slide, position)
     * @returns InsertResult
     */
    public async copyPresentationShape(
        sourceContextId: string,
        options: CopyShapeOptions
    ): Promise<InsertResult> {
        // Parse composite ID: "deck_abc|slide_257|shape_42"
        const parts = sourceContextId.split('|');
        if (parts.length < 3) {
            return {
                success: false,
                method: "copy",
                error: `Invalid composite ID format: ${sourceContextId}. Expected deck_*|slide_*|shape_*`,
            };
        }

        const [deckPart, slidePart, shapePart] = parts;
        
        // Extract slide number from slide_257 format
        const slideMatch = slidePart.match(/slide_(\d+)/);
        if (!slideMatch) {
            return {
                success: false,
                method: "copy",
                error: `Invalid slide ID format in composite ID: ${slidePart}`,
            };
        }
        const sourceSlideNum = parseInt(slideMatch[1], 10);
        
        // Extract shape ID from shape_42 format
        const shapeMatch = shapePart.match(/shape_(\d+)/);
        if (!shapeMatch) {
            return {
                success: false,
                method: "copy",
                error: `Invalid shape ID format in composite ID: ${shapePart}`,
            };
        }
        const shapeId = parseInt(shapeMatch[1], 10);

        // Resolve position with default aspect ratio (shapes may vary)
        const resolvedPosition = resolveImagePosition(options.position, 1.5);

        const targetdocumentId = options.targetdocumentId || await getOrCreatedocumentId();

        // options.slideIndex is already 1-based, use it directly for COM
        console.log(`[ArtifactManager] Copying shape ${shapeId} from slide ${sourceSlideNum} to slide ${options.slideIndex}`);

        try {
            const result = await backendClient.copyShapesCOM({
                documentId: targetdocumentId,
                targetSlideId: options.slideIndex,  // Already 1-based for COM
                sourceSlideId: sourceSlideNum,
                shapes: [{
                    shapeId,
                    targetPosition: {
                        left: resolvedPosition.left,
                        top: resolvedPosition.top,
                        width: resolvedPosition.width,
                        height: resolvedPosition.height,
                    },
                }],
            });
            
            if (!result.success) {
                console.error(`[ArtifactManager] COM copyShapes returned failure:`, result.message);
                return {
                    success: false,
                    method: "copy",
                    error: result.message || "Shape copy failed",
                    warnings: result.warnings,
                };
            }

            const newShapeId = result.copiedShapes?.[0]?.newShapeId;
            console.log(`[ArtifactManager] Copied shape, new ID: ${newShapeId}`);

            return {
                success: true,
                shapeId: newShapeId ? String(newShapeId) : undefined,
                slideIndex: options.slideIndex, // Return 1-based for consistency
                method: "copy",
                warnings: result.warnings?.length > 0 ? result.warnings : undefined,
            };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error(`[ArtifactManager] Shape copy error:`, error);
            return {
                success: false,
                method: "copy",
                error: `Shape copy failed: ${errorMsg}`,
            };
        }
    }

    // ═══════════════════════════════════════════════════════════════════════
    // METADATA AND UTILITIES
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Get artifact metadata entry from the current presentation by shape ID.
     * Useful for "right-click → go to source" functionality.
     * 
     * @param shapeId PowerPoint shape ID
     * @returns MetadataEntry or null
     */
    public async getArtifactInfoByShape(shapeId: string): Promise<MetadataEntry | null> {
        return getArtifactEntryByShape(shapeId);
    }
    
    /**
     * Check if current presentation matches expected deck ID.
     * 
     * @param expecteddocumentId The expected deck ID
     * @returns true if matches
     */
    public async verifydocumentId(expecteddocumentId: string): Promise<boolean> {
        const currentdocumentId = await getdocumentId();
        return currentdocumentId === expecteddocumentId;
    }
    
    /**
     * Get the current presentation's deck ID.
     */
    public async getCurrentdocumentId(): Promise<string> {
        return getOrCreatedocumentId();
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON ACCESSOR
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get the ArtifactManager singleton instance
 */
export function getArtifactManager(): ArtifactManager {
    return ArtifactManager.getInstance();
}

export default ArtifactManager;



